import { noop } from "lodash-es";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import {
  DragOverlay,
  TouchSensor,
  PointerSensor,
  useSensor,
  useSensors,
  MeasuringStrategy,
  DndContext,
  KeyboardSensor,
} from "@dnd-kit/core";
import { createPortal } from "react-dom";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

import Counter from "ds/components/Counter";
import { useToggle } from "hooks/useToggle";
import { PhaseName } from "utils/hooks";
import DragDropListOverlay from "components/DragDropList/Overlay";
import { dropAnimation, getCoordinateGetter } from "components/DragDropList/utils";
import useTwoColumnDND from "components/DragDropList/useTwoColumnDND";
import Item from "components/BeforeAfterCommands/List/Item";
import DragDropListDefaultItem from "components/DragDropList/DefaultItem";
import CardCollapsible from "ds/components/Card/Collapsible";

import { ContextHooks, StackHooksFormData } from "../../types";
import PhaseHooks from "../PhaseHooks";

type PhaseSectionProps = {
  name: PhaseName;
  label: string;
  contextHooks: ContextHooks;
  hasBeforeHooks: boolean;
  hasAfterHooks: boolean;
};

const PhaseSection = ({
  name,
  label,
  contextHooks,
  hasAfterHooks,
  hasBeforeHooks,
}: PhaseSectionProps) => {
  const [isCollapsed, toggle] = useToggle(true);

  const { setValue, watch } = useFormContext<StackHooksFormData>();

  const { before: contextBeforeHooks, after: contextAfterHooks } = contextHooks;
  // Default empty value for run before hooks
  const { before: stackBeforeHooks = [], after: stackAfterHooks = [] } = watch(`hooks.${name}`);

  const count =
    contextBeforeHooks.length +
    contextAfterHooks.length +
    stackBeforeHooks.length +
    stackAfterHooks.length;

  const allTextCommandsById: Record<string, string> = useMemo(() => {
    return {
      ...stackBeforeHooks.reduce((acc, command) => ({ ...acc, [command.id]: command.text }), {}),
      ...stackAfterHooks.reduce((acc, command) => ({ ...acc, [command.id]: command.text }), {}),
    };
  }, [stackBeforeHooks, stackAfterHooks]);

  const firstColumn = useMemo(() => {
    return stackBeforeHooks.map((command) => ({
      value: command.id,
    }));
  }, [stackBeforeHooks]);

  const secondColumn = useMemo(() => {
    return stackAfterHooks.map((command) => ({
      value: command.id,
    }));
  }, [stackAfterHooks]);

  const coordinateGetter = useMemo(() => getCoordinateGetter(true), []);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter,
    })
  );

  const {
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    handleDragOver,
    handleDragMove,
    collisionDetection,
    firstContainerItems,
    secondContainerItems,
    isDragActive,
    activeElementId,
  } = useTwoColumnDND({
    onChange: ({ first, second }) => {
      setValue(`hooks.${name}`, {
        before:
          first.map((item: { value: string }) => ({
            id: item.value,
            text: allTextCommandsById[item.value],
          })) || [],
        after:
          second.map((item: { value: string }) => ({
            id: item.value,
            text: allTextCommandsById[item.value],
          })) || [],
      });
    },
    firstColumn: firstColumn || [],
    secondColumn: secondColumn || [],
  });

  return (
    <CardCollapsible
      key={name}
      ariaLevel={3}
      ariaLabel={label}
      isCollapsed={isCollapsed}
      onToggle={toggle}
      title={label}
      titleSuffix={count > 0 && <Counter count={count} />}
      contentGap="large"
    >
      <DndContext
        sensors={sensors}
        onDragEnd={handleDragEnd}
        onDragAbort={handleDragCancel}
        onDragCancel={handleDragCancel}
        measuring={{
          droppable: {
            strategy: MeasuringStrategy.Always,
          },
        }}
        onDragOver={handleDragOver}
        onDragStart={handleDragStart}
        collisionDetection={collisionDetection}
        modifiers={[restrictToVerticalAxis]}
        onDragMove={handleDragMove}
      >
        {hasBeforeHooks && (
          <PhaseHooks
            isDragActive={isDragActive}
            type="before"
            name={name}
            contextHooks={contextBeforeHooks}
            stackHooksIds={firstContainerItems}
            allTextCommandsById={allTextCommandsById}
          />
        )}

        {hasAfterHooks && (
          <PhaseHooks
            isDragActive={isDragActive}
            type="after"
            name={name}
            contextHooks={contextAfterHooks}
            stackHooksIds={secondContainerItems}
            allTextCommandsById={allTextCommandsById}
          />
        )}

        {createPortal(
          <DragOverlay dropAnimation={dropAnimation}>
            {activeElementId && (
              <DragDropListOverlay
                component={
                  <DragDropListDefaultItem
                    titleComponent={
                      <Item
                        text={allTextCommandsById[activeElementId]}
                        readOnly={false}
                        onRemoveCommand={noop}
                        onEditCommand={noop}
                        id={activeElementId}
                      />
                    }
                  />
                }
              />
            )}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
    </CardCollapsible>
  );
};

export default PhaseSection;
